import React, { useState } from 'react';
import Button from './Button';
import Input from './Input';
import { useLocalization } from '../localization/LocalizationContext';

const UrlCreator: React.FC = () => {
  const { t } = useLocalization();
  const [price, setPrice] = useState('');
  const [name, setName] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastGeneratedLink, setLastGeneratedLink] = useState<string | null>(null);

  // Function to shorten URL using Short.io API
  const shortenUrl = async (url: string): Promise<string> => {
    // Only use URL shortening in production
    if (process.env.NODE_ENV !== 'production') {
      console.log('Development environment detected, skipping URL shortening');
      return url;
    }

    try {
      const apiKey = process.env.REACT_APP_SHORTIO_API_KEY;
      const domain = process.env.REACT_APP_SHORTIO_DOMAIN;

      if (!apiKey || !domain) {
        console.error('Short.io API key or domain not found in environment variables');
        return url;
      }

      const response = await fetch('https://api.short.io/links/public', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': apiKey
        },
        body: JSON.stringify({
          domain,
          originalURL: url
        })
      });

      if (!response.ok) {
        throw new Error(`Short.io API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Shortened URL:', data);

      // Return the shortened URL if available, otherwise return the original URL
      return data.shortURL || url;
    } catch (error) {
      console.error('Error shortening URL:', error);
      return url;
    }
  };

  const handleCreateUrl = async () => {
    if (!price || !name) return;

    setIsLoading(true);

    try {
      // Create URL with parameters
      const baseUrl = 'https://saleittomebaby.online/';
      const params = new URLSearchParams();
      params.append('amount', price);
      params.append('item', encodeURIComponent(name));
      const fullUrl = `${baseUrl}?${params.toString()}`;

      // In production, this will shorten the URL
      // In development, it will just return the original URL
      const finalUrl = await shortenUrl(fullUrl);

      // Store the generated link
      setLastGeneratedLink(finalUrl);

      // Copy to clipboard
      await navigator.clipboard.writeText(finalUrl);

      // Show success notification
      setShowNotification(true);

      // Clear inputs
      setPrice('');
      setName('');

      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to process URL:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-custom-pink">{t.urlCreator.title}</h1>

      <div className="bg-white shadow-sm rounded-lg border border-gray-100 p-6 mb-6">
        <div className="mb-6">
          <Input
            label={t.urlCreator.itemName.label}
            type="text"
            placeholder={t.urlCreator.itemName.placeholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            icon={
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
              </svg>
            }
          />
        </div>

        <div className="mb-6">
          <Input
            label={t.urlCreator.price.label}
            type="number"
            placeholder={t.urlCreator.price.placeholder}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            icon={
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            }
          />
        </div>

        <Button
          onClick={handleCreateUrl}
          type="button"
          disabled={isLoading || (!price || !name)}
          isLoading={isLoading}
          className="w-full"
        >
          {t.urlCreator.createButton}
        </Button>

        {lastGeneratedLink && (
          <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-sm text-gray-600 mb-2">Оля, це посилання на товар:</p>
            <div className="flex items-center">
              <input
                type="text"
                readOnly
                value={lastGeneratedLink}
                className="flex-1 p-2 text-sm bg-white border border-gray-300 rounded-md"
              />
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(lastGeneratedLink);
                  setShowNotification(true);
                  setTimeout(() => setShowNotification(false), 3000);
                }}
                className="ml-2 p-2 bg-gray-200 hover:bg-gray-300 rounded-md"
                title="Copy to clipboard"
              >
                <svg className="h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {showNotification && (
        <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{t.urlCreator.copiedNotification}</p>
            </div>
          </div>
        </div>
      )}

      {/* Deployment ID - only visible in production */}
      {process.env.NODE_ENV === 'production' && process.env.VERCEL_DEPLOYMENT_ID && (
        <div className="mt-8 text-center">
          <p className="text-[8px] text-gray-300">ID: {process.env.VERCEL_DEPLOYMENT_ID}</p>
        </div>
      )}
    </div>
  );
};

export default UrlCreator;